import React, {
  PropsWithChildren,
  useState,
  ReactNode,
  forwardRef,
  Ref,
  useImperativeHandle,
  RefObject,
  useEffect,
  useRef,
  JSX
} from 'react';
import { faCompressAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash-es';
import { Col, Collapse, Row } from 'reactstrap';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';
import useConfig from '../../Hooks/useConfig';
import { wait } from '../../../Utils/globals';
import useWindow from '../../Hooks/useWindow';
import { Events, scroller } from 'react-scroll';

interface Props {
  collapseLabel: string;
  searchContent: ReactNode;
  desktopSearchCollapse?: boolean;
  stickySearch?: boolean;
  lowerContent?: ReactNode;
  isStickyListener?: IsStickyListenerType;
  offset?: number;
  useSearchToggleButton?: boolean;
  widgetClickable?: boolean | false;
  initiallyCollapsed?: boolean;
  listTopAnchor?: string;
}

const DEFAULT_TOP_OFFSET = 0;
export type CollapsibleRefFns = {
  closeCollapsible: () => void;
  toggleCollapsible: () => void;
  openCollapsible: () => void;
};
export type CollapsibleRef = RefObject<CollapsibleRefFns | null>;
export type IsStickyListenerType = (a: {
  isSticky: boolean;
  refs: {
    containerRef?: RefObject<HTMLDivElement | null>;
    stickyRef?: RefObject<HTMLDivElement | null>;
  };
  setSearchCollapsed: (a: boolean) => void;
}) => void;

const SearchCollapsible = forwardRef<CollapsibleRefFns, PropsWithChildren<Props>>(
  (props: PropsWithChildren<Props>, ref: Ref<CollapsibleRefFns>): JSX.Element => {
    const {
      collapseLabel,
      searchContent,
      children,
      desktopSearchCollapse,
      stickySearch,
      lowerContent,
      isStickyListener,
      offset = DEFAULT_TOP_OFFSET,
      useSearchToggleButton,
      widgetClickable,
      initiallyCollapsed,
      listTopAnchor
    } = props;
    const window = useWindow();
    const config = useConfig();
    const element = !!config.stickySearchOffsetClass
      ? (window?.document?.querySelector(config.stickySearchOffsetClass) as HTMLElement)
      : undefined;

    const matches = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });

    const [searchCollapsed, setSearchCollapsed] = useState<boolean>(
      !!(initiallyCollapsed && !matches)
    );
    const [isSticky, setIsSticky] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      (): CollapsibleRefFns => ({
        closeCollapsible: (): void => {
          if (!matches || desktopSearchCollapse) {
            setSearchCollapsed(true);
          }
        },
        toggleCollapsible: (): void => {
          setSearchCollapsed(value => !value);
        },
        openCollapsible: (): void => {
          setSearchCollapsed(false);
        }
      })
    );

    useEffect(() => {
      if (!!listTopAnchor) {
        Events.scrollEvent.register('begin', to => {
          if (to === listTopAnchor) {
            setSearchCollapsed(false);
          }
        });
      }

      return () => {
        if (!!listTopAnchor) {
          Events.scrollEvent.remove('begin');
        }
      };
    }, [listTopAnchor]);

    const getContainerWidth = async (iterations: number): Promise<number> => {
      const width = containerRef.current?.clientWidth || 0;
      await wait();
      if (width !== 0 || iterations === 0) {
        return width;
      } else {
        return getContainerWidth(iterations - 1);
      }
    };

    const eventHandler = async (): Promise<void> => {
      if (!containerRef.current || !stickyRef.current) return;
      const width = await getContainerWidth(20);
      const sticky = stickyRef.current;
      if (
        (window?.scrollY || 0) >
        (window?.scrollY || 0) +
          (containerRef.current?.getBoundingClientRect().top || 0) -
          (element?.offsetHeight || 0) +
          offset
      ) {
        if (!isSticky && isStickyListener) {
          setIsSticky(true);
        }
        sticky.classList.add('search__collapsible__sticky');
        sticky.style.width = `${width}px`;
        if (element) {
          sticky.style.top = `${element.offsetHeight}px`;
          containerRef.current.style.paddingTop = `${sticky.clientHeight}px`;
        } else {
          sticky.style.top = `0`;
        }
      } else {
        if (isSticky && isStickyListener) {
          setIsSticky(false);
        }
        if (!!sticky) {
          sticky.classList.remove('search__collapsible__sticky');
          sticky.style.width = 'initial';
        }
        if (element) {
          sticky.style.top = 'initial';
        }
        if (!!containerRef.current) {
          containerRef.current.style.paddingTop = '0';
        }
      }
    };

    useEffect(() => {
      if (isStickyListener) {
        isStickyListener({
          isSticky,
          refs: { containerRef, stickyRef },
          setSearchCollapsed
        });
      }
    }, [isSticky]);

    useEffect(() => {
      const debouncedEventHandler = debounce(eventHandler, 250);
      if (stickySearch) {
        window?.removeEventListener('scroll', eventHandler);
        window?.removeEventListener('resize', debouncedEventHandler);
        window?.addEventListener('scroll', eventHandler);
        window?.addEventListener('resize', debouncedEventHandler);
      }

      return () => {
        if (stickySearch) {
          window?.removeEventListener('scroll', eventHandler);
          window?.removeEventListener('resize', debouncedEventHandler);
        }
      };
    }, [element, stickySearch, isSticky]);

    useEffect(() => {
      if (stickySearch) {
        setTimeout(() => {
          eventHandler();
        }, 400);
      }
    }, [searchCollapsed, stickySearch, containerRef, stickyRef]);

    useEffect(() => {
      if (matches && !desktopSearchCollapse) {
        setSearchCollapsed(false);
      }
    }, [matches]);

    const handleToggle = (): void => {
      setSearchCollapsed(value => !value);
    };

    const clickableWidget = (): void => {
      if (widgetClickable) {
        setSearchCollapsed(false);
      }
    };

    return (
      <div ref={containerRef} className="position-relative h-100">
        {!matches && useSearchToggleButton && !searchCollapsed && (
          <div className="search-collapsible__toggle" onClick={handleToggle}>
            <FontAwesomeIcon icon={faCompressAlt} />
          </div>
        )}
        <div ref={stickyRef} className="search-collapsible__outer">
          <Collapse
            className={widgetClickable ? 'search-collapsible__pointer' : ''}
            isOpen={searchCollapsed}
            onClick={clickableWidget}
          >
            <Row>
              <Col className="search-collapsible d-flex justify-content-between">
                <div className="pr-2">{searchContent}</div>
                <button
                  type="button"
                  className="link-button"
                  onClick={(): void => {
                    if (!!listTopAnchor) {
                      scroller.scrollTo(listTopAnchor, {
                        duration: 500,
                        smooth: true,
                        offset: -50
                      });
                    } else {
                      setSearchCollapsed(false);
                    }
                  }}
                >
                  {collapseLabel}
                </button>
              </Col>
            </Row>
          </Collapse>
          <Collapse isOpen={!searchCollapsed}>{children}</Collapse>
        </div>
        {!!lowerContent && lowerContent}
      </div>
    );
  }
);

export default SearchCollapsible;
